import React, { Suspense } from 'react';
import ReactDOM from 'react-dom';
import {
  Router, Switch, Route,
} from 'react-router-dom';
import { ApolloProvider } from '@apollo/client';
import * as Sentry from '@sentry/react';
import { Integrations } from '@sentry/tracing';
import { Provider } from 'react-redux';
import { CssBaseline, StylesProvider, ThemeProvider } from '@material-ui/core';
import { LoadingSpinner, EnvironmentSwitcher } from '@bighealth/react-limbix-ui';
import { PostHogProvider } from 'posthog-js/react';
import posthog from 'posthog-js';

import App from './App';
import history from './utils/history';
import './i18n';
import { client } from './apollo';
import { oldTheme } from './theme';
import store from './store';
import './index.css';

import AppStoreRedirector from './pages/Onboarding/components/AppStoreRedirector/AppStoreRedirector';
import PostHogPageviewTracker from './PostHogPageViewTracker';
import {
  projectApiKey, postHogHost, beforeSend, stripSensitiveUrlParams,
} from './utils/posthog';
import { detokenize } from './utils/tokenizer';
import { logError } from './utils/logError';
import { UserUidProvider } from './hooks/useUserUid';

import Resources from './pages/Resources';

import UtmCapture from './components/UtmCapture/UtmCapture';
import OrgSlugCapture from './components/OrgSlugCapture/OrgSlugCapture';
import ProgramCapture from './components/ProgramCapture/ProgramCapture';
import SpringIdCapture from './components/SpringIdCapture/SpringIdCapture';
import { SpringIdProvider } from './hooks/useSpringId';
import SingularInit from './components/SingularInit';
import DatadogInit from './components/DatadogInit';

import CheckYourOrganization from './pages/PreOnboarding/steps/CheckYourOrganization/CheckYourOrganization';
import LoginPhone from './pages/SparkLogin/LoginPhone';
import LoginEmail from './pages/SparkLogin/LoginEmail';
import AccessCodeSpark from './pages/PreOnboarding/steps/AccessCodeSpark';
import AccessCodeEntry from './pages/PreOnboarding/steps/AccessCodeSpark/AccessCodeEntry';
import CreateAccount from './pages/PreOnboarding/steps/AccessCodeSpark/CreateAccount';
import SmsVerify from './pages/PreOnboarding/steps/AccessCodeSpark/SmsVerify';
import UserInfoConfirmation from './pages/PreOnboarding/steps/AccessCodeSpark/UserInfoConfirmation';
import EmailAuthPage from './pages/PreOnboarding/steps/EmailAuthPage/EmailAuthPage';
import MagicLinkSentPage from './pages/PreOnboarding/steps/MagicLinkSentPage/MagicLinkSentPage';
import EmailAuthCallback from './pages/PreOnboarding/steps/EmailAuthCallback/EmailAuthCallback';
import ActivationEligibilityCheck from
  './pages/PreOnboarding/steps/ActivationEligibilityCheck/ActivationEligibilityCheck';
import DownloadSparkApp from './pages/PreOnboarding/steps/DownloadSparkApp/DownloadSparkApp';
import EmployerDependentSelectionPage from
  './pages/PreOnboarding/steps/EmployerDependentSelection/EmployerDependentSelectionPage';
import ActivationRouter from './pages/PreOnboarding/steps/ActivateSleepio/ActivationRouter';
import EmployerSelection from './pages/PreOnboarding/steps/ActivateSleepio/steps/EmployerSelection';

import {
  Login,
  ResetPassword,
  ResetPasswordForm,
  ResetPasswordFormRedirect,
  KaiserSignupPage,
  Onboarding,
  SleepioWellnessOnboarding,
  PreOnboarding,
  SoleraAccountCreation,
  SingularLanding,
  SpringHealthEnrollment,
} from '@/pages';

if (process.env.NODE_ENV !== 'development') {
  Sentry.init({
    dsn: 'https://2ef2b1df37e1499a8f1d971534713649@o58970.ingest.sentry.io/5275036',
    integrations: [new Integrations.BrowserTracing()],
    tracesSampleRate: 1.0,
    environment: process.env.REACT_APP_DEPLOY_ENV || 'unknown',
    release: `parents@${process.env.APP_VERSION}`,
    // Same tokens the app-handoff bounce can leak into PostHog - strip them here too.
    beforeSend: (event) => {
      if (event.request?.url) {
        Object.assign(event.request, { url: stripSensitiveUrlParams(event.request.url) });
      }
      return event;
    },
  });
}

if (projectApiKey && typeof window !== 'undefined') {
  posthog.init(projectApiKey, {
    api_host: postHogHost,
    // don't double capture pageviews, this is handled by PostHogPageviewTracker
    capture_pageview: false,
    // strips auth tokens that may leak via the app-handoff bounce into several URL-bearing fields
    before_send: beforeSend,
  });

  // Stitch the Spark app's PostHog identity into this CAP browser session.
  // Spark passes ph_distinct_id (plaintext) or ph_token (tokenized) in the URL.
  // Identifying here — before any React component mounts — ensures every CAP
  // event fires under Spark's ID, and posthog.get_distinct_id() returns it
  // throughout the session without each component needing to read the URL param.
  // ph_distinct_id takes precedence (backward compat); ph_token requires an
  // async detokenization call and identifies when that resolves.
  const urlParams = new URLSearchParams(window.location.search);
  const phDistinctId = urlParams.get('ph_distinct_id')?.trim();
  const phToken = urlParams.get('ph_token')?.trim();

  if (phDistinctId) {
    posthog.identify(phDistinctId);
  } else if (phToken) {
    detokenize(phToken).then((result) => {
      if (result.success && result.value) {
        posthog.identify(result.value);
      }
    }).catch((error) => {
      logError('[PostHog]', 'ph_token detokenization failed', error, {
        tags: { feature: 'posthog-identity-detokenization' },
        extra: { phToken },
      });
    });
  }
}

const AppRoutes: React.FC = () => (
  <Switch>
    <Route component={Login} path="/login" />
    <Route
      exact
      path="/spark/login"
      component={LoginPhone}
    />
    <Route
      exact
      path="/spark/login/email"
      component={LoginEmail}
    />
    <Route component={Resources} path="/:product/resources" />
    <Route component={ResetPassword} path="/reset-password" />
    <Route component={ResetPasswordForm} path="/reset-password-form/:userUid/:token" />
    <Route component={ResetPasswordFormRedirect} path="/reset-password-form-redirect" />
    <Route component={KaiserSignupPage} path="/organization-signup/kaiser" />
    <Route
      component={SleepioWellnessOnboarding}
      exact
      path="/coverage/:product/:slug/:careTeamUid/:token"
    />
    <Route
      component={SoleraAccountCreation}
      exact
      path="/solera/:product"
    />
    <Route
      component={EmployerDependentSelectionPage}
      exact
      path="/activate/:product/employers/:org_slug/dependents"
    />
    <Route
      component={CreateAccount}
      exact
      path="/activate/:product/employers/:org_slug/dependents/create-account"
    />
    <Route
      component={CheckYourOrganization}
      path="/activate/:product/employers"
    />
    <Route
      component={CheckYourOrganization}
      path="/activate/:product/providers"
    />
    <Route
      component={AccessCodeSpark}
      exact
      path="/activate/:product/access-code"
    />
    <Route
      component={AccessCodeEntry}
      exact
      path="/activate/:product/access-code/entry"
    />
    <Route
      component={CreateAccount}
      exact
      path="/activate/:product/access-code/entry/create-account"
    />
    <Route
      component={EmailAuthPage}
      exact
      path="/activate/:product/email-auth"
    />
    <Route
      component={MagicLinkSentPage}
      exact
      path="/activate/:product/check-email"
    />
    <Route
      component={EmailAuthCallback}
      exact
      path="/:product/:lob/activate/email/callback"
    />
    <Route
      component={SmsVerify}
      exact
      path="/activate/:product/sms/verify"
    />
    <Route
      component={ActivationEligibilityCheck}
      exact
      path="/activate/:product/coverage-check/:org_slug"
    />
    <Route
      component={UserInfoConfirmation}
      exact
      path="/activate/:product/user-info/confirmation"
    />
    <Route
      component={DownloadSparkApp}
      exact
      path="/activate/:product/download-app"
    />
    <Route component={Onboarding} path="/coverage/:product/:slug" />
    <Route
      component={SpringHealthEnrollment}
      exact
      path="/activate/spring-health/:product"
    />
    <Route exact path="/activate/sleepio-canopy/wellness" component={EmployerSelection} />
    <Route path="/activate/sleepio-canopy/:lob/:org_slug/sign-up" component={ActivationRouter} />
    <Route path="/activate/sleepio-canopy/:lob/:org_slug/login" component={ActivationRouter} />
    <Route component={PreOnboarding} path="/activate/:product/:slug" />
    <Route component={AppStoreRedirector} path="/appstore/:product" />
    <Route component={SingularLanding} exact path="/s" />
    <Route component={App} />
  </Switch>
);

ReactDOM.render(
  <React.StrictMode>
    <PostHogProvider client={posthog}>
      <StylesProvider injectFirst>
        <ThemeProvider theme={oldTheme}>
          <CssBaseline />
          <Provider store={store}>
            <Suspense fallback={<LoadingSpinner />}>
              <ApolloProvider client={client}>
                <UserUidProvider>
                  <SpringIdProvider>
                    <Router history={history}>
                      <UtmCapture />
                      <OrgSlugCapture />
                      <ProgramCapture />
                      <SpringIdCapture />
                      <PostHogPageviewTracker />
                      <SingularInit />
                      <DatadogInit />
                      <AppRoutes />
                      <EnvironmentSwitcher />
                    </Router>
                  </SpringIdProvider>
                </UserUidProvider>
              </ApolloProvider>
            </Suspense>
          </Provider>
        </ThemeProvider>
      </StylesProvider>
    </PostHogProvider>
  </React.StrictMode>,
  document.getElementById('root'),
);
